import { css } from '@emotion/react';
import { AccordionGroup, LinearProgress, styled } from '@mui/joy';
import { useLocation } from '@tanstack/react-router';
import type { Point } from 'geojson';
import type { MapLayerEventType } from 'maplibre-gl';
import { Fragment, lazy, Suspense, useCallback } from 'react';

import { CreateReportStart } from '@/components/create-report/CreateReportStart';
import { MapContentProvider } from '@/components/map/MapContentProvider';
import { MapSheet } from '@/components/map/MapSheet';
import { useMapFlyTo } from '@/hooks/useMapFlyTo';
import { useMapOnEvent } from '@/hooks/useMapOnEvent';
import { useScreenSize } from '@/hooks/useScreenSize';
import { MAIN_BREAKPOINT } from '@/modules/MAIN_BREAKPOINT';
import { useCreateReportStore } from '@/stores/useCreateReportStore';
import { CreateReportStep } from '@/types/CreateReportStep';
import { LocationInputMode } from '@/types/LocationInputMode';
import { transientOptions } from '@/utils/transient-options';

const CreateReportMarker = lazy(async () => ({
  default: (await import('@/components/create-report/1-location/CreateReportMarker')).CreateReportMarker,
}));

const CreateReportFinishedModal = lazy(async () => ({
  default: (await import('@/components/modals/CreateReportFinishedModal')).CreateReportFinishedModal,
}));
const CreateReportEditLocationModal = lazy(async () => ({
  default: (await import('@/components/modals/CreateReportEditLocationModal')).CreateReportEditLocationModal,
}));

const CreateReportStepLocation = lazy(async () => ({
  default: (await import('@/components/create-report/1-location/CreateReportStepLocation')).CreateReportStepLocation,
}));
const CreateReportStepDuplicates = lazy(async () => ({
  default: (await import('@/components/create-report/2-duplicates/CreateReportStepDuplicates'))
    .CreateReportStepDuplicates,
}));
const CreateReportStepCategory = lazy(async () => ({
  default: (await import('@/components/create-report/3-category/CreateReportStepCategory')).CreateReportStepCategory,
}));
const CreateReportStepDescription = lazy(async () => ({
  default: (await import('@/components/create-report/4-description/CreateReportStepDescription'))
    .CreateReportStepDescription,
}));
const CreateReportStepConfirm = lazy(async () => ({
  default: (await import('@/components/create-report/5-confirm/CreateReportStepConfirm')).CreateReportStepConfirm,
}));

const CREATE_REPORT_STEPS = [
  CreateReportStepLocation,
  CreateReportStepDuplicates,
  CreateReportStepCategory,
  CreateReportStepDescription,
  CreateReportStepConfirm,
];

const StyledMapContentProvider = styled(
  MapContentProvider,
  transientOptions,
)<{ $fullHeight?: boolean }>(
  ({ theme, $fullHeight }) => css`
    right: 0;
    bottom: 0;
    left: 0;

    ${theme.breakpoints.up(MAIN_BREAKPOINT)} {
      right: auto;
    }

    ${theme.breakpoints.up(MAIN_BREAKPOINT)} and (max-height: 799px) {
      bottom: ${$fullHeight ? 0 : 'auto'};
      top: 0;
    }

    ${theme.breakpoints.up(MAIN_BREAKPOINT)} and (min-height: 800px) {
      bottom: ${$fullHeight ? theme.spacing(4) : 'auto'};
      top: ${theme.spacing(4)};
    }
  `,
);

const StyledMapSheet = styled(
  MapSheet,
  transientOptions,
)<{ $expanded: boolean }>(
  ({ theme, $expanded }) => css`
    ${theme.breakpoints.up(MAIN_BREAKPOINT)} {
      border-top-left-radius: 0;
    }

    ${theme.breakpoints.up(MAIN_BREAKPOINT)} and (max-height: 799px) {
      border-top-right-radius: 0;
      border-bottom-right-radius: ${$expanded ? 0 : theme.radius.xl};
    }

    ${theme.breakpoints.up(MAIN_BREAKPOINT)} and (min-height: 800px) {
      max-height: calc(100svh - ${theme.spacing(15)});
      border-bottom-right-radius: ${theme.radius.xl};
    }
  `,
);

export function CreateReport() {
  const expanded = useCreateReportStore((state) => state.expanded);
  const step = useCreateReportStore((state) => state.step);
  const finished = useCreateReportStore((state) => state.finished);
  const editLocation = useCreateReportStore((state) => state.editLocation);
  const geometry = useCreateReportStore((state) => state.geometry);
  const locationInputMode = useCreateReportStore((state) => state.locationInputMode);

  const { setLocationInputMode, updateGeometryWithReverseGeocoding, setExpanded, setEditLocation } =
    useCreateReportStore((state) => state.actions);

  const { isDesktop, isMobile } = useScreenSize();

  const Step = CREATE_REPORT_STEPS[step];

  const location = useLocation();

  useMapFlyTo({ geometry, enabled: expanded });

  const handleRightClick = useCallback(
    async (event: MapLayerEventType['contextmenu']) => {
      if (locationInputMode === LocationInputMode.MAP) {
        setLocationInputMode(LocationInputMode.DEFAULT);
      } else {
        setExpanded(true);
        const { lngLat } = event;
        const newGeometry: Point = { type: 'Point', coordinates: lngLat.toArray() };

        if (step === CreateReportStep.LOCATION) {
          setLocationInputMode(LocationInputMode.DEFAULT);
          await updateGeometryWithReverseGeocoding(newGeometry);
        } else {
          setEditLocation(newGeometry);
        }
      }
    },
    [locationInputMode, setLocationInputMode, setExpanded, step, updateGeometryWithReverseGeocoding, setEditLocation],
  );

  useMapOnEvent({
    type: 'contextmenu',
    listener: handleRightClick,
  });

  return (
    <Fragment>
      {expanded ? (
        <Suspense>
          <CreateReportMarker />
        </Suspense>
      ) : null}
      {finished ? (
        <Suspense>
          <CreateReportFinishedModal />
        </Suspense>
      ) : null}
      {editLocation ? (
        <Suspense>
          <CreateReportEditLocationModal />
        </Suspense>
      ) : null}

      {isDesktop || location.pathname === '/' ? (
        <StyledMapContentProvider $fullHeight={expanded} placement={{ mobile: 'bottom', desktop: 'left' }}>
          {isMobile && !expanded ? (
            <CreateReportStart />
          ) : (
            <StyledMapSheet $expanded={expanded}>
              {isMobile && Step ? (
                <Suspense>
                  <Step />
                </Suspense>
              ) : null}

              {isDesktop ? (
                <Fragment>
                  <CreateReportStart />

                  {expanded ? (
                    <Suspense fallback={<LinearProgress sx={{ '--LinearProgress-radius': 0 }} variant="solid" />}>
                      <AccordionGroup sx={{ '--ListItem-paddingX': '24px' }}>
                        {CREATE_REPORT_STEPS.map((DesktopStep, index) => (
                          <DesktopStep // eslint-disable-next-line react/no-array-index-key
                            key={index}
                          />
                        ))}
                      </AccordionGroup>
                    </Suspense>
                  ) : null}
                </Fragment>
              ) : null}
            </StyledMapSheet>
          )}
        </StyledMapContentProvider>
      ) : null}
    </Fragment>
  );
}
